// Global Instructions Rule Applied!
//
// Company KPIs section (A1, owner-approved placement 2026-08-18: Dashboard
// left column under the Company Brief — "option B" + As-of column +
// per-datapoint tooltips). One compact row per KPI: name, current value with
// delta vs the previous point, As-of date, and a sparkline whose datapoints
// are hoverable circles (AntD Tooltip: period + formatted value). Values are
// agent-extracted and auto-approved; anomalies ride as amber markers (the
// kpi_anomaly signal is the attention channel). Renders nothing until the
// company has at least one recorded value.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchCompanyKpis } from '../services/internalAgentsService.ts'
import {
  CompanyKpi,
  CompanyKpiHistoryPoint
} from '../../../state_manager/data_models/internal_agents_model.ts'

interface CompanyKpisSectionProps {
  accountId: string  /** Hands `load` to the company form so its Refresh button reloads this
   *  panel. Same contract DocumentsTab exposes via onRefreshReady. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', SGD: 'S$' }

const compactNumber = (value: number): string => {
  const abs = Math.abs(value)
  const trim = (n: number) => {
    const rounded = abs >= 100e6 || (abs >= 100e3 && abs < 1e6) ? Math.round(n) : Math.round(n * 10) / 10
    return String(rounded)
  }
  if (abs >= 1e9) return `${trim(value / 1e9)}B`
  if (abs >= 1e6) return `${trim(value / 1e6)}M`
  if (abs >= 1e3) return `${trim(value / 1e3)}k`
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100)
}

const formatValue = (value: number, unitType: string, currencyCode: string | null): string => {
  if (unitType === 'percent') return `${Math.round(value * 10) / 10}%`
  if (unitType === 'months') return `${Math.round(value * 10) / 10} mo`
  if (unitType === 'currency') {
    const symbol = CURRENCY_SYMBOLS[currencyCode ?? ''] ?? (currencyCode ? `${currencyCode} ` : '$')
    return `${symbol}${compactNumber(value)}`
  }
  return compactNumber(value)
}

// Delta vs the previous history point, colored by the KPI's cfg direction
// (a burn increase is bad even though the number went up).
const Delta: React.FC<{ kpi: CompanyKpi }> = ({ kpi }) => {
  const history = kpi.history
  if (history.length < 2) return null
  const prev = history[history.length - 2].value_numeric
  const curr = history[history.length - 1].value_numeric
  if (prev === 0 || prev == null) return null
  const pct = ((curr - prev) / Math.abs(prev)) * 100
  if (!Number.isFinite(pct) || Math.abs(pct) < 0.05) return null
  const arrow = pct > 0 ? '▲' : '▼'
  const improving =
    kpi.direction === 'lower_better' ? pct < 0 : kpi.direction === 'higher_better' ? pct > 0 : null
  const color =
    improving === null ? 'text-gray-400' : improving ? 'text-green-600' : 'text-red-600'
  return (
    <span className={`${color} text-[11px] ml-1.5`}>
      {arrow} {pct > 0 ? '+' : ''}
      {Math.round(pct * 10) / 10}%
    </span>
  )
}

// Inline sparkline: polyline + one hoverable circle per datapoint (owner ask
// 2026-08-18). Tooltips carry the period + formatted value; anomalous points
// render amber; the latest point is emphasized.
const Sparkline: React.FC<{ kpi: CompanyKpi }> = ({ kpi }) => {
  const width = 110
  const height = 26
  const pad = 4
  const points = kpi.history
  if (points.length === 0) return null

  const values = points.map((p) => p.value_numeric)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const coords = points.map((p, i) => ({
    point: p,
    x: points.length === 1 ? width / 2 : pad + (i * (width - 2 * pad)) / (points.length - 1),
    y: height - pad - ((p.value_numeric - min) / span) * (height - 2 * pad)
  }))
  const polyline = coords.map((c) => `${c.x},${c.y}`).join(' ')

  const pointTitle = (p: CompanyKpiHistoryPoint) =>
    `${dayjs(p.period_end).format('MM/DD/YYYY')} · ${formatValue(
      p.value_numeric,
      kpi.unit_type,
      kpi.account_kpis__currency_code
    )}${p.anomaly ? ' · out-of-band' : ''}`

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className='block overflow-visible'
      aria-label={`${kpi.kpi_label} trend`}
    >
      {coords.length > 1 ? (
        <polyline points={polyline} fill='none' stroke='#4C90E9' strokeWidth={1.6} />
      ) : null}
      {coords.map((c, i) => {
        const isLast = i === coords.length - 1
        const color = c.point.anomaly ? '#d97706' : '#4C90E9'
        return (
          <Tooltip key={c.point.period_end} title={pointTitle(c.point)} mouseEnterDelay={0.05}>
            <circle
              cx={c.x}
              cy={c.y}
              r={isLast ? 3 : 2.2}
              fill={color}
              stroke='#ffffff'
              strokeWidth={0.8}
              className='cursor-pointer'
            />
          </Tooltip>
        )
      })}
    </svg>
  )
}

export const CompanyKpisSection: React.FC<CompanyKpisSectionProps> = ({
  accountId,
  onRefreshReady = null
}) => {
  const [kpis, setKpis] = useState<CompanyKpi[]>([])
  const [loading, setLoading] = useState(true)
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setKpis(await fetchCompanyKpis(accountId, token()))
    } catch (error) {
      console.error('[internal-agents] company kpis load failed:', error)
      setKpis([])
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale. `onRefreshReady` hands `load` up to the
  // company form so its Refresh button reloads this panel too (same contract as
  // DocumentsTab / FileUploadManager).
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  // Only KPIs with at least one approved value render; the section hides
  // entirely until the first extraction lands (quiet companies keep today's
  // Dashboard).
  const rows = useMemo(() => kpis.filter((k) => k.current != null), [kpis])

  if (!loading && rows.length === 0) {
    return null
  }

  return (
    <FormSection
      data-component='CompanyKpisSection'
      title='KPIs'
      titleColor='blue'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerActions={<Tag color='blue'>Agent-extracted</Tag>}
    >
      <div className='overflow-x-auto'>
        <table className='w-full' style={{ fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr className='text-left text-[10.5px] uppercase tracking-wide text-gray-400'>
              <th className='font-semibold py-1 pr-2'>KPI</th>
              <th className='font-semibold py-1 pr-2 text-right'>Current</th>
              <th className='font-semibold py-1 px-2'>As of</th>
              <th className='font-semibold py-1 px-2'>Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((kpi) => {
              const current = kpi.current
              if (!current) return null
              return (
                <tr key={kpi.account_kpis__id} className='border-t border-gray-100'>
                  <td className='py-1.5 pr-2'>
                    <span className='font-semibold text-[12.5px]'>{kpi.kpi_label}</span>
                    {kpi.is_custom ? (
                      <Tag color='purple' className='ml-1.5 text-[10px] leading-4 px-1'>
                        custom
                      </Tag>
                    ) : null}
                  </td>
                  <td className='py-1.5 pr-2 text-right whitespace-nowrap'>
                    <Tooltip
                      title={current.company_kpi_values__value_raw ?? undefined}
                      mouseEnterDelay={0.4}
                    >
                      <span className='font-semibold text-[13px]'>
                        {formatValue(
                          current.company_kpi_values__value_numeric,
                          kpi.unit_type,
                          current.company_kpi_values__currency_code ??
                            kpi.account_kpis__currency_code
                        )}
                      </span>
                    </Tooltip>
                    <Delta kpi={kpi} />
                    {current.company_kpi_values__anomaly ? (
                      <Tooltip title={current.company_kpi_values__anomaly_reason ?? 'out-of-band vs history'}>
                        <Tag color='gold' className='ml-1.5 text-[10px] leading-4 px-1'>
                          out-of-band
                        </Tag>
                      </Tooltip>
                    ) : null}
                  </td>
                  <td className='py-1.5 px-2 text-gray-500 text-[11.5px] whitespace-nowrap'>
                    {dayjs(current.company_kpi_values__period_end).format('MM/DD/YYYY')}
                  </td>
                  <td className='py-1.5 px-2'>
                    <Sparkline kpi={kpi} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </FormSection>
  )
}
